import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Layout from '../../components/layout/main';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fade,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import BiotechRoundedIcon from '@mui/icons-material/BiotechRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';
import { Formik, Form, FieldArray } from 'formik';
import { AuthContext } from '../../context/auth';
import { getDataFull, getData, postData, putData, deleteData } from '../../services/ajax';
import { AlertModalContext } from '../../components/modal/alert';
import { LoadingModalContext } from '../../components/modal/loading';
import MaterialTable, { DataType } from '../../components/material-table';
import { blue, green, red } from '@mui/material/colors';

const validationSchema = yup.object({
    nombre: yup.string().trim().required('El nombre es obligatorio').max(255, 'Máximo 255 caracteres'),
    id_determinacion_grupo: yup
        .number()
        .typeError('Elegí un sector')
        .required('Elegí un sector')
        .integer(),
    detalles: yup
        .array()
        .of(
            yup.object({
                id_codigo: yup.mixed().nullable().required('Elegí un código'),
                cantidad: yup
                    .number()
                    .typeError('Cantidad inválida')
                    .integer('Usá un número entero')
                    .min(1, 'Mínimo 1')
                    .max(9999, 'Máximo 9999')
                    .required('Indicá la cantidad'),
            })
        )
        .min(1, 'Agregá al menos un código'),
});

const lineaVacia = { id_codigo: null, cantidad: 1 };

function codigoLabel(c) {
    if (!c) return '';
    const desc = c.categoria_descripcion ? ` — ${c.categoria_descripcion}` : '';
    return `${c.codigo}${desc}`;
}

function DeterminacionPage() {
    const theme = useTheme();
    const { usuario } = useContext(AuthContext);
    const { enqueueSnackbar } = useSnackbar();
    const alertModal = useContext(AlertModalContext);
    const loadingModal = useContext(LoadingModalContext);

    const puedeGestionar = usuario?.nivel_usuario === 1 || usuario?.sup_usuarios === true;

    const [rowsTableMain, setRowsTableMain] = useState(null);
    const [tableMainNeedUpdate, setTableMainNeedUpdate] = useState(Date.now());

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [gruposOptions, setGruposOptions] = useState([]);
    const [codigosOptions, setCodigosOptions] = useState([]);

    useEffect(() => {
        if (!dialogOpen || !usuario?.token) return;
        let cancelled = false;
        (async () => {
            const [g, c] = await Promise.all([
                getDataFull('determinacion-grupos?per_page=500&order_by=nombre&order=asc', usuario.token),
                getDataFull('codigos?per_page=500&order_by=codigo&order=asc&para_determinacion=1', usuario.token),
            ]);
            if (cancelled) return;
            if (g.error === '') setGruposOptions(Array.isArray(g.data) ? g.data : []);
            else enqueueSnackbar(g.error, { variant: 'error' });
            if (c.error === '') setCodigosOptions(Array.isArray(c.data) ? c.data : []);
            else enqueueSnackbar(c.error, { variant: 'error' });
        })();
        return () => {
            cancelled = true;
        };
    }, [dialogOpen, usuario?.token, enqueueSnackbar]);

    const openNuevo = () => {
        setEditing(null);
        setDialogOpen(true);
    };

    const openEditar = useCallback(
        async (row) => {
            loadingModal.setOpen(true);
            const res = await getData(`determinaciones/${row.id}`, usuario.token);
            loadingModal.setOpen(false);
            if (res.error) {
                enqueueSnackbar(res.error, { variant: 'error' });
                return;
            }
            setEditing(res.data);
            setDialogOpen(true);
        },
        [usuario?.token, loadingModal, enqueueSnackbar]
    );

    const cerrarDialog = () => {
        setDialogOpen(false);
        setEditing(null);
    };

    const buildPayload = (values) => ({
        nombre: values.nombre.trim(),
        id_determinacion_grupo: Number(values.id_determinacion_grupo),
        detalles: values.detalles.map((line) => ({
            id_codigo: typeof line.id_codigo === 'object' && line.id_codigo != null ? line.id_codigo.id : Number(line.id_codigo),
            cantidad: Number(line.cantidad) || 1,
        })),
    });

    const onSubmitForm = async (values, { setSubmitting, resetForm }) => {
        loadingModal.setOpen(true);
        const payload = buildPayload(values);
        let res;
        if (editing) {
            res = await putData(`determinaciones/${editing.id}`, payload, usuario.token);
        } else {
            res = await postData('determinaciones', payload, usuario.token);
        }
        loadingModal.setOpen(false);
        setSubmitting(false);

        if (res.error) {
            enqueueSnackbar(res.error, { variant: 'error' });
            return;
        }
        enqueueSnackbar(editing ? 'Cambios guardados' : 'Determinación creada', { variant: 'success' });
        resetForm();
        cerrarDialog();
        setTableMainNeedUpdate(Date.now());
    };

    const onEliminar = (row) => {
        alertModal.showModal(
            '¿Eliminar esta determinación?',
            `Se borrará "${row.nombre}" y su composición de códigos. Esta acción no se puede deshacer.`,
            async () => {
                loadingModal.setOpen(true);
                const res = await deleteData(`determinaciones/${row.id}`, usuario.token);
                loadingModal.setOpen(false);
                if (res.error) enqueueSnackbar(res.error, { variant: 'error' });
                else {
                    enqueueSnackbar('Determinación eliminada', { variant: 'success' });
                    setTableMainNeedUpdate(Date.now());
                }
            }
        );
    };

    const onEditarFromTable = useCallback(
        (row) => {
            openEditar(row);
        },
        [openEditar]
    );

    const onEliminarFromTable = useCallback(
        (row) => {
            onEliminar(row);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [usuario?.token]
    );

    const tableMain = useMemo(
        () => ({
            title: 'Listado de determinaciones',
            idName: 'id',
            defaultOrder: 'asc',
            headers: [
                { id: 'id', label: 'Nº', dataType: DataType.NUMBER, sortable: true },
                { id: 'nombre', label: 'Nombre', dataType: DataType.TEXT, sortable: true },
                { id: 'grupo_nombre', label: 'Sector', dataType: DataType.TEXT, sortable: true },
                { id: 'resumen_codigos', label: 'Códigos (cantidad)', dataType: DataType.TEXT, sortable: false },
                { id: 'arancel_total', label: 'Arancel ref.', dataType: DataType.MONEY, sortable: false },
            ],
            loadData: async (orderBy, order, page, rowsPerPage, searchText, selectedFilter, pageUpdate, totalUpdate, filterListUpdate) => {
                setRowsTableMain(null);
                const apiPage = page + 1;
                const params = new URLSearchParams();
                params.append('per_page', rowsPerPage.toString());
                params.append('page', apiPage.toString());
                if (orderBy) params.append('order_by', orderBy);
                if (order) params.append('order', order);
                if (searchText) params.append('search_text', searchText);

                const response = await getDataFull(`determinaciones?${params.toString()}`, usuario.token);

                if (response.error === '') {
                    const list = (response.data || []).map((row) => ({
                        ...row,
                        grupo_nombre: row.grupo?.nombre ?? '',
                        arancel_total: row.arancel_total != null ? Number(row.arancel_total) : null,
                    }));
                    const tablePage = response.meta?.page ? response.meta.page - 1 : page;
                    pageUpdate(tablePage);
                    totalUpdate(response.meta?.total || 0);
                    filterListUpdate([]);
                    setRowsTableMain(list);
                } else {
                    enqueueSnackbar(response.error, { variant: 'error' });
                    setRowsTableMain([]);
                }
            },
            moreMenuItems: puedeGestionar
                ? [
                      {
                          label: 'Nueva determinación',
                          secondary: 'Nombre, sector y líneas de código con cantidad',
                          icon: <AddRoundedIcon sx={{ color: green[600] }} />,
                          itemColor: green[600],
                          action: () => openNuevo(),
                      },
                  ]
                : [],
            rowActions: puedeGestionar
                ? (row) => [
                      {
                          label: 'Editar',
                          secondary: 'Cambiar sector, nombre o composición de códigos',
                          icon: <EditRoundedIcon sx={{ color: blue[600] }} />,
                          itemColor: blue[600],
                          action: () => onEditarFromTable(row),
                      },
                      {
                          label: 'Eliminar',
                          secondary: 'Quita la determinación del catálogo',
                          icon: <DeleteOutlineRoundedIcon sx={{ color: red[600] }} />,
                          itemColor: red[600],
                          dividerBefore: true,
                          action: () => onEliminarFromTable(row),
                      },
                  ]
                : () => [],
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [usuario?.token, puedeGestionar, enqueueSnackbar]
    );

    const heroGradient = useMemo(
        () =>
            theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.35)} 0%, ${alpha('#1a237e', 0.5)} 50%, ${alpha(theme.palette.secondary.main, 0.25)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha('#e8f4fc', 1)} 45%, ${alpha(theme.palette.secondary.main, 0.15)} 100%)`,
        [theme]
    );

    const initialForm = useMemo(() => {
        if (!editing) {
            return {
                nombre: '',
                id_determinacion_grupo: '',
                detalles: [lineaVacia],
            };
        }
        return {
            nombre: editing.nombre ?? '',
            id_determinacion_grupo: editing.id_determinacion_grupo ?? '',
            detalles:
                Array.isArray(editing.detalles) && editing.detalles.length > 0
                    ? editing.detalles.map((d) => ({
                          id_codigo: d.codigo ?? null,
                          cantidad: d.cantidad ?? 1,
                      }))
                    : [lineaVacia],
        };
    }, [editing]);

    return (
        <Layout nombrePagina="Determinaciones">
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Fade in timeout={500}>
                        <Box
                            sx={{
                                position: 'relative',
                                overflow: 'hidden',
                                borderRadius: 3,
                                p: { xs: 2, md: 3 },
                                background: heroGradient,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            }}
                        >
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                                        color: 'primary.main',
                                    }}
                                >
                                    <BiotechRoundedIcon sx={{ fontSize: 28 }} />
                                </Box>
                                <Box flex={1}>
                                    <Typography variant="h6" fontWeight={700}>
                                        Determinaciones de laboratorio
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Cada determinación tiene un <strong>nombre</strong>, pertenece a un <strong>sector</strong> (por ejemplo Hematología o Serología) y se compone de uno o más{' '}
                                        <strong>códigos de práctica</strong> del nomenclador, cada uno con una <strong>cantidad</strong> (cuántas veces aplica ese ítem). El arancel de referencia suma
                                        cantidad × arancel de cada código cuando el arancel está cargado. Ejemplos: Hemograma con categoría I ×6; coagulograma I ×4 e II ×2; brucelosis con I, II y IV una vez
                                        cada uno; Dengue con el código 38.01 ×1.
                                    </Typography>
                                </Box>
                                {puedeGestionar && (
                                    <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openNuevo} sx={{ borderRadius: 2, flexShrink: 0 }}>
                                        Nueva determinación
                                    </Button>
                                )}
                            </Stack>
                        </Box>
                    </Fade>
                </Grid>

                <Grid size={12}>
                    <Accordion defaultExpanded={false} disableGutters elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TipsAndUpdatesRoundedIcon color="info" fontSize="small" />
                                <Typography fontWeight={600}>Cómo usar esta pantalla</Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                            <Typography variant="body2" color="text.secondary" component="div">
                                <strong>Buscar:</strong> por nombre de determinación o por nombre de sector. <strong>Ordenar:</strong> columnas con encabezado clicable. El <strong>arancel ref.</strong> aplica reglas del
                                nomenclador (p. ej. 40.01 se liquida con 1.03 y 1.05 según cantidad; esos códigos de liquidación no se eligen acá, solo existen en el catálogo para referencia). <strong>Alta / edición:</strong> sector,
                                nombre y líneas de código con cantidad. Si repetís el mismo código en dos líneas, al guardar se unifican. <strong>Eliminar:</strong> borra la determinación y sus líneas; no borra los códigos del catálogo.
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                </Grid>

                <Grid size={12}>
                    <Fade in timeout={400}>
                        <Box>
                            <MaterialTable
                                title={tableMain.title}
                                loadData={tableMain.loadData}
                                headers={tableMain.headers}
                                data={rowsTableMain}
                                moreMenuItems={tableMain.moreMenuItems}
                                selectable={false}
                                rowActions={puedeGestionar ? tableMain.rowActions : null}
                                idName={tableMain.idName}
                                defaultOrder={tableMain.defaultOrder}
                                defaultRowsPerPage={10}
                                needUpdate={tableMainNeedUpdate}
                                dense
                            />
                        </Box>
                    </Fade>
                </Grid>
            </Grid>

            <Dialog open={dialogOpen} onClose={cerrarDialog} fullWidth maxWidth="md" TransitionProps={{ timeout: 400 }}>
                <DialogTitle sx={{ pb: 1 }}>{editing ? 'Editar determinación' : 'Nueva determinación'}</DialogTitle>
                <Formik
                    initialValues={initialForm}
                    validationSchema={validationSchema}
                    enableReinitialize
                    onSubmit={onSubmitForm}
                >
                    {({ isSubmitting, values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
                        <>
                            <DialogContent sx={{ maxHeight: '70vh' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Definí el sector, el nombre comercial de la prestación y las líneas de códigos con su cantidad (veces que se aplica ese ítem en la determinación).
                                </Typography>
                                <Form id="form-determinacion">
                                    <Stack spacing={2.5}>
                                        <TextField
                                            name="nombre"
                                            label="Nombre"
                                            placeholder="Ej.: Hemograma, Coagulograma, Dengue…"
                                            fullWidth
                                            variant="outlined"
                                            autoFocus={!editing}
                                            value={values.nombre}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={Boolean(touched.nombre && errors.nombre)}
                                            helperText={touched.nombre && errors.nombre ? errors.nombre : undefined}
                                        />
                                        <Autocomplete
                                            options={gruposOptions}
                                            getOptionLabel={(o) => o?.nombre ?? ''}
                                            isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                            value={gruposOptions.find((g) => g.id === values.id_determinacion_grupo) ?? null}
                                            onChange={(_, v) => setFieldValue('id_determinacion_grupo', v?.id ?? '')}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Sector"
                                                    placeholder="Buscar sector…"
                                                    error={Boolean(touched.id_determinacion_grupo && errors.id_determinacion_grupo)}
                                                    helperText={
                                                        touched.id_determinacion_grupo && errors.id_determinacion_grupo
                                                            ? errors.id_determinacion_grupo
                                                            : undefined
                                                    }
                                                />
                                            )}
                                        />

                                        <FieldArray name="detalles">
                                            {(helpers) => (
                                                <Stack spacing={1.5}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Códigos de práctica
                                                    </Typography>
                                                    {values.detalles.map((line, index) => (
                                                        <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                                                            <Autocomplete
                                                                sx={{ flex: 1, minWidth: 0 }}
                                                                options={codigosOptions}
                                                                getOptionLabel={codigoLabel}
                                                                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                                                value={
                                                                    typeof line.id_codigo === 'object' && line.id_codigo != null
                                                                        ? line.id_codigo
                                                                        : codigosOptions.find((c) => c.id === line.id_codigo) ?? null
                                                                }
                                                                onChange={(_, v) => setFieldValue(`detalles.${index}.id_codigo`, v)}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Código"
                                                                        placeholder="Nomenclador…"
                                                                        error={Boolean(
                                                                            touched.detalles?.[index]?.id_codigo && errors.detalles?.[index]?.id_codigo
                                                                        )}
                                                                        helperText={
                                                                            touched.detalles?.[index]?.id_codigo && errors.detalles?.[index]?.id_codigo
                                                                                ? errors.detalles[index].id_codigo
                                                                                : undefined
                                                                        }
                                                                    />
                                                                )}
                                                            />
                                                            <TextField
                                                                name={`detalles.${index}.cantidad`}
                                                                label="Cantidad"
                                                                type="number"
                                                                inputProps={{ min: 1, max: 9999 }}
                                                                sx={{ width: { xs: '100%', sm: 120 } }}
                                                                value={line.cantidad}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={Boolean(touched.detalles?.[index]?.cantidad && errors.detalles?.[index]?.cantidad)}
                                                                helperText={
                                                                    touched.detalles?.[index]?.cantidad && errors.detalles?.[index]?.cantidad
                                                                        ? errors.detalles[index].cantidad
                                                                        : undefined
                                                                }
                                                            />
                                                            <Stack direction="row" spacing={0} sx={{ pt: { sm: 0.5 } }}>
                                                                <IconButton
                                                                    aria-label="Quitar línea"
                                                                    disabled={values.detalles.length <= 1}
                                                                    onClick={() => helpers.remove(index)}
                                                                    size="small"
                                                                    color="error"
                                                                >
                                                                    <RemoveCircleOutlineRoundedIcon />
                                                                </IconButton>
                                                            </Stack>
                                                        </Stack>
                                                    ))}
                                                    <Button
                                                        type="button"
                                                        size="small"
                                                        startIcon={<AddCircleOutlineRoundedIcon />}
                                                        onClick={() => helpers.push({ ...lineaVacia })}
                                                        sx={{ alignSelf: 'flex-start' }}
                                                    >
                                                        Agregar código
                                                    </Button>
                                                    {typeof errors.detalles === 'string' && (
                                                        <Typography variant="caption" color="error">
                                                            {errors.detalles}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            )}
                                        </FieldArray>
                                    </Stack>
                                </Form>
                            </DialogContent>
                            <DialogActions sx={{ px: 3, pb: 2 }}>
                                <Button onClick={cerrarDialog} color="inherit">
                                    Cancelar
                                </Button>
                                <Button variant="contained" disabled={isSubmitting} size="large" type="submit" form="form-determinacion">
                                    {editing ? 'Guardar cambios' : 'Crear determinación'}
                                </Button>
                            </DialogActions>
                        </>
                    )}
                </Formik>
            </Dialog>
        </Layout>
    );
}

export default DeterminacionPage;
